import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ArrowUpRight, Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { getToken, setSession } from "@/lib/auth";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (getToken()) {
      navigate("/admin", { replace: true });
    }
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Please enter email and password.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", form);
      setSession(data.token, data.email);
      toast.success("Welcome back.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = err?.response?.data?.detail || "Invalid email or password.";
      toast.error(typeof msg === "string" ? msg : "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-[#F8FAFC] flex items-center justify-center px-6 py-24">
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-[#0A2540] text-white items-center justify-center mb-5">
            <Lock size={22} strokeWidth={1.5} />
          </div>
          <p className="uppercase text-xs tracking-[0.25em] font-semibold text-[#0284C7]">
            Indo-Pacific Group
          </p>
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-medium text-slate-900 mt-3">
            Owner Login
          </h1>
          <p className="text-sm text-slate-600 mt-3 max-w-sm mx-auto">
            Restricted area — sign in to view contact enquiries and appointment requests.
          </p>
        </div>

        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="rounded-3xl bg-white border border-slate-100 p-8 shadow-[0_8px_30px_rgba(10,37,64,0.06)] space-y-5"
        >
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Email</label>
            <Input
              data-testid="admin-login-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@email.com"
              className="mt-2 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
              autoComplete="email"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold">Password</label>
            <div className="relative">
              <Input
                data-testid="admin-login-password"
                type={showPwd ? "text" : "password"}
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••"
                className="mt-2 h-12 rounded-xl border-slate-200 pr-12 focus-visible:ring-2 focus-visible:ring-[#0A2540]/20"
                autoComplete="current-password"
              />
              <button
                type="button"
                data-testid="admin-login-toggle-pwd"
                onClick={() => setShowPwd((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 mt-1 text-slate-500 hover:text-slate-800"
                aria-label="Toggle password visibility"
              >
                {showPwd ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
          <Button
            data-testid="admin-login-submit"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-[#0A2540] hover:bg-[#0F172A] text-white h-12"
          >
            {loading ? "Signing in..." : "Sign In"}
            <ArrowUpRight size={16} strokeWidth={1.5} className="ml-1" />
          </Button>
        </form>

        <p className="text-center text-xs text-slate-500 mt-8">
          Only the website owner can access this area.
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;
